import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  TextField,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  OutlinedInput,
  FormHelperText,
  Typography,
  Paper,
  CircularProgress,
  Snackbar,
  IconButton,
  Rating,
  Alert,
  Switch,
  FormControlLabel,
  Dialog,
  DialogContent
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { useTheme } from '@mui/material/styles';
import axios from '../../api/axios';
import ImageUpload from './ImageUpload'; // Assuming ImageUpload component is in the same directory
import { debounce } from 'lodash';

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
  anchorOrigin: {
    vertical: 'bottom',
    horizontal: 'left',
  },
  transformOrigin: {
    vertical: 'top',
    horizontal: 'left',
  }
};

// Date formatter function
const formatDateForInput = (dateString) => {
  if (!dateString) return '';
  try {
    const date = new Date(dateString);
    return date.toISOString().split('T')[0];
  } catch (error) {
    return '';
  }
};

const genres = [
  'Action',
  'Adventure',
  'Animation',
  'Comedy',
  'Drama',
  'Horror',
  'Romance',
  'Sci-Fi',
  'Thriller',
  'Others',
  'Mystery',
  'Documentary',
  'Fantasy',
  'Biography',
  'Crime',
];

const categories = [
  'Hollywood',
  'Bollywood',
  'South Indian',
  'Latest Release',
  'Trending Now',
  'Nepali',
  'Korean',
  'Japanese',
  'Italian',
  'Others'
];

const languages = [
  'English',
  'Hindi',
  'Tamil',
  'Telugu',
  'Malayalam',
  'Kannada',
  'Bengali',
  'Marathi',
  'Punjabi',
  'Nepali',
  'Japanese',
  'Korean',
  'Bhojpuri',
  'Maithli',
  'Others',
];

const qualities = [
  '4K',
  '1080p',
  '720p',
  '480p',
  '360p'
];

const initialState = {
  title: '',
  description: '',
  releaseDate: '',
  categories: [],
  genres: [],
  movieLanguage: '',
  duration: '',
  quality: '',
  size: '',
  director: '',
  year: new Date().getFullYear(),
  cast: [],
  rating: 0,
  featured: false,
  watchLinks: [{ quality: '', url: '' }],
  downloadLinks: [],
  images: []
};

const MovieForm = ({ mode = 'add', initialData = null, onSubmit }) => {
  const theme = useTheme();
  const [formData, setFormData] = useState(() => {
    const data = initialData || initialState;
    return {
      ...initialState,
      ...data,
      categories: data.categories || [],
      genres: data.genres || [],
      movieLanguage: data.movieLanguage || '',
      watchLinks: data.watchLinks?.length ? data.watchLinks : [{ quality: '', url: '' }],
      downloadLinks: data.downloadLinks || [],
      cast: data.cast || [],
      images: data.images || []
    };
  });

  const [touched, setTouched] = useState({}); // Track which fields have been touched
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null); // Add back submitError state
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  useEffect(() => {
    if (mode === 'edit' && initialData) {
      console.log('Initial form data:', initialData);
      
      const formattedData = {
        ...initialData,
        _id: initialData._id,
        title: initialData.title || '',
        description: initialData.description || '',
        releaseDate: initialData.releaseDate ? new Date(initialData.releaseDate).toISOString().split('T')[0] : '',
        categories: initialData.categories || [],
        genres: initialData.genres || [],
        movieLanguage: initialData.movieLanguage || '',
        duration: initialData.duration || '',
        quality: initialData.quality || '',
        size: initialData.size || '',
        director: initialData.director || '',
        year: initialData.year || new Date().getFullYear(),
        cast: initialData.cast || [],
        rating: initialData.rating || 0,
        featured: Boolean(initialData.featured),
        watchLinks: initialData.watchLinks || [{ quality: '', url: '' }],
        downloadLinks: initialData.downloadLinks || [],
        images: initialData.images || []
      };

      console.log('Formatted form data:', formattedData);
      setFormData(formattedData);
    }
  }, [mode, initialData]);

  useEffect(() => {
    console.log('Current form data:', formData);
  }, [formData]);

  // Format date for display
  const formatDate = (date) => {
    if (!date) return '-';
    try {
      return new Date(date).toLocaleDateString();
    } catch (error) {
      return '-';
    }
  };

  // Validate all fields and return all errors
  const validateForm = () => {
    const errors = {};
    
    // Required fields validation
    if (!formData.title?.trim()) {
      errors.title = 'Title is required';
    }
    if (!formData.description?.trim()) {
      errors.description = 'Description is required';
    }
    if (!formData.categories?.length) {
      errors.categories = 'At least one category is required';
    }
    if (!formData.genres?.length) {
      errors.genres = 'At least one genre is required';
    }
    if (!formData.downloadLinks?.length || !formData.downloadLinks.some(link => link.url && link.quality)) {
      errors.downloadLinks = 'At least one download link is required';
    }
    if (!formData.images?.length) {
      errors.images = 'At least one image is required';
    }

    // URL validations
    formData.downloadLinks?.forEach((link, index) => {
      if (link.url && !(/^(http|https):\/\/[^ "]+$/.test(link.url) || link.url.startsWith('magnet:'))) {
        errors[`downloadLinks.${index}.url`] = 'Invalid URL or magnet link format';
      }
    });

    formData.watchLinks?.forEach((link, index) => {
      if (link.url && !/^(http|https):\/\/[^ "]+$/.test(link.url)) {
        errors[`watchLinks.${index}.url`] = 'Invalid URL format';
      }
    });

    // Rating validation
    if (formData.rating && (isNaN(formData.rating) || formData.rating < 0 || formData.rating > 10)) {
      errors.rating = 'Rating must be between 0 and 10';
    }

    return errors;
  };

  // Validate all required fields
  const validateField = (name, value) => {
    if (!name) return '';
    
    // Only validate required fields
    switch (name) {
      case 'title':
        if (!value?.trim()) return 'Title is required';
        if (value.length < 2) return 'Title must be at least 2 characters';
        break;
      case 'description':
        if (!value?.trim()) return 'Description is required';
        if (value.length < 10) return 'Description must be at least 10 characters';
        break;
      case 'categories':
        if (!value?.length) return 'At least one category is required';
        break;
      case 'genres':
        if (!value?.length) return 'At least one genre is required';
        break;
      case 'images':
        if (!value?.length) return 'At least one image is required';
        break;
      case 'watchLinks':
        if (!value?.length || !value.some(link => link.url && link.quality)) {
          return 'At least one watch link with quality is required';
        }
        break;
      case 'movieLanguage':
        if (!value) return 'Language is required';
        break;
      // Optional fields - no validation needed
      case 'releaseDate':
      case 'duration':
      case 'quality':
      case 'size':
      case 'director':
      case 'cast':
      case 'downloadLinks':
      case 'year':
      case 'rating':
      case 'featured':
        return '';
    }
    return '';
  };

  // Handle field change with immediate validation
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    setTouched(prev => ({ ...prev, [name]: true }));
    
    const error = validateField(name, value);
    setErrors(prev => ({ ...prev, [name]: error }));
  };

  // Handle blur with validation
  const handleBlur = (event) => {
    // Handle both event objects and direct field names
    const name = typeof event === 'string' ? event : event?.target?.name;
    const value = typeof event === 'string' ? formData[event] : event?.target?.value;
    
    if (!name) return; // Skip if no name is provided
    
    setTouched(prev => ({ ...prev, [name]: true }));
    const error = validateField(name, value);
    setErrors(prev => ({ ...prev, [name]: error }));
  };

  // Handle multi-select change with validation
  const handleMultiSelectChange = (event) => {
    const { name, value } = event.target;
    setFormData(prev => ({
      ...prev,
      [name]: Array.isArray(value) ? value : []
    }));
    setTouched(prev => ({ ...prev, [name]: true }));
    
    const error = validateField(name, value);
    setErrors(prev => ({ ...prev, [name]: error }));
  };

  // Handle watch link changes with validation
  const handleWatchLinkChange = (index, field, value) => {
    const newLinks = [...formData.watchLinks];
    newLinks[index] = { 
      ...newLinks[index], 
      [field]: field === 'url' && value && !value.startsWith('http') ? 'https://' + value : value 
    };

    setFormData(prev => ({
      ...prev,
      watchLinks: newLinks
    }));
    setTouched(prev => ({ ...prev, watchLinks: true }));
    
    const error = validateField('watchLinks', newLinks);
    setErrors(prev => ({ ...prev, watchLinks: error }));
  };

  const handleDownloadLinkChange = (index, field, value) => {
    const newLinks = [...formData.downloadLinks];
    newLinks[index] = { 
      ...newLinks[index], 
      [field]: field === 'url' && value && !value.startsWith('http') ? 'https://' + value : value 
    };

    setFormData(prev => ({
      ...prev,
      downloadLinks: newLinks
    }));
  };

  const addDownloadLink = () => {
    setFormData(prev => ({
      ...prev,
      downloadLinks: [...prev.downloadLinks, { quality: '', url: '' }]
    }));
  };

  const addWatchLink = () => {
    setFormData(prev => ({
      ...prev,
      watchLinks: [...prev.watchLinks, { quality: '', url: '' }]
    }));
  };

  const removeDownloadLink = (index) => {
    setFormData(prev => ({
      ...prev,
      downloadLinks: prev.downloadLinks.filter((_, i) => i !== index)
    }));
  };

  const removeWatchLink = (index) => {
    setFormData(prev => ({
      ...prev,
      watchLinks: prev.watchLinks.filter((_, i) => i !== index)
    }));
  };

  const handleCastChange = (index, field, value) => {
    const newCast = [...formData.cast];
    newCast[index] = {
      ...newCast[index],
      [field]: value
    };
    setFormData(prev => ({
      ...prev,
      cast: newCast
    }));
  };

  const addCastMember = () => {
    setFormData(prev => ({
      ...prev,
      cast: [...prev.cast, { name: '', role: '' }]
    }));
  };

  const removeCastMember = (index) => {
    setFormData(prev => ({
      ...prev,
      cast: prev.cast.filter((_, i) => i !== index)
    }));
  };

  const handleImageChange = async (event) => {
    const files = Array.from(event.target.files);
    if (!files.length) return;

    // Validate file sizes and types
    const maxSize = 5 * 1024 * 1024; // 5MB
    const validTypes = ['image/jpeg', 'image/png', 'image/webp'];
    
    const invalidFiles = files.filter(file => {
      if (file.size > maxSize) {
        setErrors(prev => ({
          ...prev,
          images: `File ${file.name} is too large. Maximum size is 5MB`
        }));
        return true;
      }
      if (!validTypes.includes(file.type)) {
        setErrors(prev => ({
          ...prev,
          images: `File ${file.name} must be JPEG, PNG, or WebP`
        }));
        return true;
      }
      return false;
    });

    if (invalidFiles.length > 0) return;

    setFormData(prev => ({
      ...prev,
      images: [...prev.images, ...files]
    }));
    setTouched(prev => ({ ...prev, images: true }));
    setErrors(prev => ({ ...prev, images: null }));
  };

  const handleRemoveImage = (index) => {
    setFormData(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index)
    }));
  };

  const handleDateChange = (date) => {
    setFormData(prev => ({
      ...prev,
      releaseDate: date
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setIsSubmitting(true);
      setSubmitError(null);

      // Validate form
      const validationErrors = validateForm();
      if (Object.keys(validationErrors).length > 0) {
        setErrors(validationErrors);
        setIsSubmitting(false);
        showSnackbar('Please fix the validation errors', 'error');
        return;
      }

      // Upload images if they're files
      const uploadedImages = await Promise.all(
        formData.images.map(async (image) => {
          if (image instanceof File) {
            const imageData = new FormData();
            imageData.append('file', image);
            imageData.append('upload_preset', import.meta.env.VITE_CLOUDINARY_UPLOAD_PRESET);
            imageData.append('folder', 'moviehub/movies');

            try {
              const uploadResponse = await fetch(
                `https://api.cloudinary.com/v1_1/${import.meta.env.VITE_CLOUDINARY_CLOUD_NAME}/image/upload`,
                {
                  method: 'POST',
                  body: imageData
                }
              );

              if (!uploadResponse.ok) {
                throw new Error('Failed to upload image');
              }

              const uploadResult = await uploadResponse.json();
              return {
                url: uploadResult.secure_url,
                public_id: uploadResult.public_id
              };
            } catch (error) {
              console.error('Image upload error:', error);
              throw new Error('Failed to upload image: ' + error.message);
            }
          }
          return typeof image === 'string' ? { url: image } : image;
        })
      );

      // Format and validate the data before submission
      const submitData = {
        ...formData,
        title: formData.title?.trim(),
        description: formData.description?.trim(),
        releaseDate: formData.releaseDate || new Date().toISOString(),
        categories: formData.categories || [],
        genres: formData.genres || [],
        cast: formData.cast?.filter(member => member.name) || [],
        rating: parseFloat(formData.rating) || 0,
        watchLinks: formData.watchLinks?.filter(link => link.url && link.quality) || [],
        downloadLinks: formData.downloadLinks?.filter(link => link.url && link.quality) || [],
        movieLanguage: formData.movieLanguage || 'English',
        quality: formData.quality || 'HD',
        featured: Boolean(formData.featured),
        images: uploadedImages
      };

      // Final validation check
      if (!submitData.downloadLinks.length) {
        throw new Error('At least one download link is required');
      }
      if (!submitData.images.length) {
        throw new Error('At least one image is required');
      }

      await onSubmit(submitData);
      showSnackbar('Movie saved successfully!', 'success');
    } catch (error) {
      console.error('Form submission error:', error);
      const errorMessage = error.response?.data?.error || error.message || 'Error submitting form';
      setSubmitError(errorMessage);
      showSnackbar(errorMessage, 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSnackbarClose = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({
      open: true,
      message,
      severity
    });
  };

  return (
    <Box
      component="form"
      onSubmit={handleSubmit}
      sx={{
        width: '100%',
        maxWidth: '100%',
        p: 2
      }}
    >
      <Dialog
        open={isSubmitting}
        aria-labelledby="loading-dialog-title"
        PaperProps={{
          sx: {
            backgroundColor: 'transparent',
            boxShadow: 'none',
            overflow: 'hidden'
          }
        }}
        BackdropProps={{
          sx: {
            backgroundColor: 'rgba(0, 0, 0, 0.8)'
          }
        }}
      >
        <DialogContent>
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            p={3}
          >
            <CircularProgress color="primary" />
            <Typography variant="body1" color="white" mt={2}>
              Saving movie...
            </Typography>
          </Box>
        </DialogContent>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
      <Grid container spacing={2}>
        {/* Basic Information */}
        <Grid item xs={12}>
          <Typography variant="h6" gutterBottom>
            Basic Information
          </Typography>
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            required
            fullWidth
            id="movie-title"
            name="title"
            label="Title"
            value={formData.title}
            onChange={handleChange}
            onBlur={handleBlur}
            error={Boolean(errors.title)}
            helperText={errors.title || ''}
            inputProps={{
              'aria-label': 'Movie title',
              'aria-required': 'true'
            }}
          />
        </Grid>

        <Grid item xs={12}>
          <TextField
            required
            fullWidth
            id="movie-description"
            name="description"
            label="Description"
            value={formData.description}
            onChange={handleChange}
            onBlur={handleBlur}
            multiline
            rows={4}
            error={Boolean(errors.description)}
            helperText={errors.description || ''}
            inputProps={{
              'aria-label': 'Movie description',
              'aria-required': 'true'
            }}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <FormControl 
            fullWidth 
            error={touched.movieLanguage && !!errors.movieLanguage}
            required
          >
            <InputLabel>Language</InputLabel>
            <Select
              name="movieLanguage"
              value={formData.movieLanguage}
              onChange={handleChange}
              onBlur={() => handleBlur('movieLanguage')}
              label="Language"
            >
              {languages.map((lang) => (
                <MenuItem key={lang} value={lang}>
                  {lang}
                </MenuItem>
              ))}
            </Select>
            {touched.movieLanguage && errors.movieLanguage && (
              <FormHelperText>{errors.movieLanguage}</FormHelperText>
            )}
          </FormControl>
        </Grid>

        <Grid item xs={12} sm={6}>
          <FormControl 
            fullWidth 
            error={touched.categories && !!errors.categories}
            required
          >
            <InputLabel>Categories</InputLabel>
            <Select
              multiple
              name="categories"
              value={formData.categories || []}
              onChange={handleMultiSelectChange}
              onBlur={() => handleBlur('categories')}
              label="Categories"
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {(selected || []).map((value) => (
                    <Chip key={value} label={value} />
                  ))}
                </Box>
              )}
            >
              {categories.map((category) => (
                <MenuItem key={category} value={category}>
                  {category}
                </MenuItem>
              ))}
            </Select>
            {touched.categories && errors.categories && (
              <FormHelperText>{errors.categories}</FormHelperText>
            )}
          </FormControl>
        </Grid>

        <Grid item xs={12} sm={6}>
          <FormControl 
            fullWidth 
            error={touched.genres && !!errors.genres}
            required
          >
            <InputLabel>Genres</InputLabel>
            <Select
              multiple
              name="genres"
              value={formData.genres || []}
              onChange={handleMultiSelectChange}
              onBlur={() => handleBlur('genres')}
              label="Genres"
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {(selected || []).map((value) => (
                    <Chip key={value} label={value} />
                  ))}
                </Box>
              )}
            >
              {genres.map((genre) => (
                <MenuItem key={genre} value={genre}>
                  {genre}
                </MenuItem>
              ))}
            </Select>
            {touched.genres && errors.genres && (
              <FormHelperText>{errors.genres}</FormHelperText>
            )}
          </FormControl>
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
          required
            fullWidth
            type="date"
            name="releaseDate"
            label="Release Date"
            value={formatDateForInput(formData.releaseDate)}
            onChange={handleChange}
            onBlur={() => handleBlur('releaseDate')}
            error={touched.releaseDate && !!errors.releaseDate}
            helperText={touched.releaseDate && errors.releaseDate}
            InputLabelProps={{ shrink: true }}
            sx={{ mb: 2 }}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            id="movie-duration"
            name="duration"
            label="Duration"
            value={formData.duration || ''}
            onChange={handleChange}
            placeholder="2h 30min"
            inputProps={{
              'aria-label': 'Movie duration'
            }}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            id="movie-quality"
            name="quality"
            label="Quality"
            value={formData.quality || ''}
            onChange={handleChange}
            placeholder="e.g., 1080p, 4K"
            inputProps={{
              'aria-label': 'Movie quality'
            }}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            id="movie-size"
            name="size"
            label="Size"
            value={formData.size || ''}
            onChange={handleChange}
            placeholder="e.g., 1.2GB"
            inputProps={{
              'aria-label': 'Movie size'
            }}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            id="movie-director"
            name="director"
            label="Director"
            value={formData.director || ''}
            onChange={handleChange}
            inputProps={{
              'aria-label': 'Movie director'
            }}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            id="movie-year"
            name="year"
            label="Year"
            type="number"
            value={formData.year || ''}
            onChange={handleChange}
            inputProps={{
              'aria-label': 'Movie year'
            }}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <FormControlLabel
            control={
              <Switch
                id="movie-featured"
                name="featured"
                checked={formData.featured || false}
                onChange={(e) => handleChange({
                  target: {
                    name: 'featured',
                    value: e.target.checked
                  }
                })}
                aria-label="Feature this movie"
              />
            }
            label="Featured"
          />
        </Grid>

        {/* Rating */}
        <Grid item xs={12} sm={6}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography component="legend">Rating</Typography>
            <Rating
              name="rating"
              value={Number(formData.rating) || 0}
              onChange={(event, newValue) => {
                setFormData(prev => ({
                  ...prev,
                  rating: newValue
                }));
              }}
              precision={0.5}
            />
          </Box>
        </Grid>

        {/* Cast Members */}
        <Grid item xs={12}>
          <Typography variant="h6" gutterBottom>
            Cast
          </Typography>
          {formData.cast.map((member, index) => (
            <Box key={index} sx={{ mb: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={5}>
                  <TextField
                    fullWidth
                    id={`cast-name-${index}`}
                    label="Actor Name"
                    value={member.name}
                    onChange={(e) => handleCastChange(index, 'name', e.target.value)}
                    onBlur={(e) => handleCastChange(index, 'name', e.target.value)}
                  />
                </Grid>
                <Grid item xs={5}>
                  <TextField
                    fullWidth
                    id={`cast-role-${index}`}
                    label="Role"
                    value={member.role}
                    onChange={(e) => handleCastChange(index, 'role', e.target.value)}
                    onBlur={(e) => handleCastChange(index, 'role', e.target.value)}
                  />
                </Grid>
                <Grid item xs={2}>
                  <Button
                    color="error"
                    onClick={() => removeCastMember(index)}
                    disabled={formData.cast.length === 1}
                  >
                    Remove
                  </Button>
                </Grid>
              </Grid>
            </Box>
          ))}
          <Button onClick={addCastMember}>Add Cast Member</Button>
        </Grid>

        {/* Watch Links */}
        <Grid item xs={12}>
          <Typography variant="h6" gutterBottom>
            Watch Links (Required)
          </Typography>
          {formData.watchLinks.map((link, index) => (
            <Box key={index} sx={{ display: 'flex', gap: 2, mb: 2 }}>
              <FormControl sx={{ width: '30%' }}>
                <InputLabel>Quality</InputLabel>
                <Select
                  value={link.quality}
                  onChange={(e) => handleWatchLinkChange(index, 'quality', e.target.value)}
                  onBlur={(e) => handleWatchLinkChange(index, 'quality', e.target.value)}
                  required
                >
                  {qualities.map((q) => (
                    <MenuItem key={q} value={q}>{q}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                sx={{ flex: 1 }}
                label="Watch URL"
                value={link.url}
                onChange={(e) => handleWatchLinkChange(index, 'url', e.target.value)}
                onBlur={(e) => handleWatchLinkChange(index, 'url', e.target.value)}
                required
              />
              <IconButton 
                color="error" 
                onClick={() => removeWatchLink(index)}
                disabled={formData.watchLinks.length === 1}
              >
                <DeleteIcon />
              </IconButton>
            </Box>
          ))}
          <Button
            startIcon={<AddIcon />}
            onClick={addWatchLink}
            variant="outlined"
            sx={{ mt: 1 }}
          >
            Add Watch Link
          </Button>
          {errors.watchLinks && (
            <FormHelperText error>{errors.watchLinks}</FormHelperText>
          )}
        </Grid>

        {/* Download Links */}
        <Grid item xs={12}>
          <Typography variant="h6" gutterBottom>
            Download Links (Required)
          </Typography>
          {formData.downloadLinks.map((link, index) => (
            <Box key={index} sx={{ display: 'flex', gap: 2, mb: 2 }}>
              <FormControl sx={{ width: '30%' }}>
                <InputLabel>Quality</InputLabel>
                <Select
                  value={link.quality}
                  onChange={(e) => handleDownloadLinkChange(index, 'quality', e.target.value)}
                  onBlur={(e) => handleDownloadLinkChange(index, 'quality', e.target.value)}
                  required
                >
                  {qualities.map((q) => (
                    <MenuItem key={q} value={q}>{q}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                sx={{ flex: 1 }}
                label="Download URL"
                value={link.url}
                onChange={(e) => handleDownloadLinkChange(index, 'url', e.target.value)}
                onBlur={(e) => handleDownloadLinkChange(index, 'url', e.target.value)}
                required
              />
              <IconButton 
                color="error" 
                onClick={() => removeDownloadLink(index)}
                disabled={formData.downloadLinks.length === 1}
              >
                <DeleteIcon />
              </IconButton>
            </Box>
          ))}
          <Button
            startIcon={<AddIcon />}
            onClick={addDownloadLink}
            variant="outlined"
            sx={{ mt: 1 }}
          >
            Add Download Link
          </Button>
          {errors.downloadLinks && (
            <FormHelperText error>{errors.downloadLinks}</FormHelperText>
          )}
        </Grid>

        {/* Images */}
        <Grid item xs={12}>
          <Box mb={2}>
            <Typography variant="h6" gutterBottom>
              Images
            </Typography>
            {errors.images && (
              <FormHelperText error>
                {errors.images}
              </FormHelperText>
            )}
          </Box>
          <ImageUpload
            images={formData.images}
            onChange={(newImages) => {
              setFormData(prev => ({ ...prev, images: newImages }));
              setTouched(prev => ({ ...prev, images: true }));
            }}
            error={touched.images && errors.images}
          />
        </Grid>

        {/* Submit Button */}
        <Grid item xs={12}>
          <Box sx={{ position: 'relative' }}>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={isSubmitting}
              fullWidth
              aria-label={`${mode === 'add' ? 'Add' : 'Update'} movie`}
            >
              {isSubmitting ? 'Submitting...' : mode === 'add' ? 'Add Movie' : 'Update Movie'}
            </Button>
            {isSubmitting && (
              <CircularProgress
                size={24}
                sx={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  marginTop: '-12px',
                  marginLeft: '-12px',
                }}
                aria-label="Submitting form"
              />
            )}
          </Box>
        </Grid>
      </Grid>

      {/* Success/Error Messages */}
      <Snackbar 
        open={!!submitError || snackbar.open} 
        autoHideDuration={6000} 
        onClose={() => {
          setSubmitError('');
          setSnackbar(prev => ({ ...prev, open: false }));
        }}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          severity={submitError ? 'error' : snackbar.severity}
          onClose={() => {
            setSubmitError('');
            setSnackbar(prev => ({ ...prev, open: false }));
          }}
          sx={{ width: '100%' }}
          role="status"
        >
          {submitError || snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default MovieForm;
